// External libraries
import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";

// UITK
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";

// Components
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { isVariantEnabled, Experiment } from "components/utility/experiment";
import { useHotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";

// Types
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";

interface ButtonTrackedProps {
  ["data-testid"]: string;
  moduleName: string;
  rfrr: string;
  text: string;
  rel: string | undefined;
  href: string | undefined;
  onClick?: () => void;
  linkName: string;
}
const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

export const SeeAllButton: React.FC<{}> = () => {
  const { formatText } = useLocalization();
  const {
    model: { showMoreStrategy, seeAllLink, seeAllType, locationName, totalHotels, view: moduleView, name: moduleName },
    context,
  } = useHotelsContext();
  const { searchContext } = context;

  if (showMoreStrategy !== "button" && showMoreStrategy !== "affinityMessage") {
    return null;
  }

  const affinityDescription =
    context && context.searchContext && context.searchContext.affinity && context.searchContext.affinity.description;
  const affinityCode = context?.searchContext?.affinity?.code;

  /* istanbul ignore next */
  const scrollTo = (): void => {
    const wizardElement = document.getElementById("wizard-hotel-pwa-1");

    if (wizardElement) {
      wizardElement.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
      wizardElement.focus();
    }
  };

  let weGotMoreMessage;
  let btnTrackedProps: ButtonTrackedProps = {
    ["data-testid"]: "hotels-cta-button",
    moduleName,
    rfrr: seeAllType,
    text: formatText("hotels.viewAllProperties"),
    rel: "nofollow",
    href: buildLinkToSRP(context) || new HotelSearchLinkBuilder(seeAllLink).build(context),
    linkName: seeAllLink,
  };

  if (moduleView === "affinity") {
    const affinityHotelCount = searchContext.affinity && searchContext.affinity.hotelCount;
    weGotMoreMessage = formatText(`hotels.${affinityCode}.gotMoreProperties`, totalHotels || affinityHotelCount);
    btnTrackedProps = {
      ...btnTrackedProps,
      text: formatText(`hotels.${affinityCode}.seeAllAvailableProperties`),
      rel: undefined,
      href: undefined,
      onClick: scrollTo,
      linkName: "Call to action click event in hotels module",
    };
  } else if (affinityDescription) {
    weGotMoreMessage = formatText("hotels.affinity.gotMoreProperties", affinityDescription, locationName);
  } else {
    weGotMoreMessage = formatText("hotels.seeAllAvailableProperties", locationName);
  }

  return (
    <UitkSpacing margin={{ block: "four" }}>
      <UitkLayoutFlex alignItems="center" direction="column" justifyContent="center">
        <UitkSpacing margin={{ blockstart: "three" }}>
          <UitkText size={300} weight="bold" theme="primary">
            {formatText("hotels.lookingFor")}
          </UitkText>
        </UitkSpacing>
        {weGotMoreMessage && <div className="uitk-type-300">{weGotMoreMessage}</div>}

        <UitkSpacing margin={{ blockstart: "three", blockend: "three" }}>
          <ButtonTracked {...btnTrackedProps} />
        </UitkSpacing>
      </UitkLayoutFlex>
    </UitkSpacing>
  );
};

/**
 * Manually builds a link to SRP with +30 days ahead and respective filters
 * Only available for SRP Lite Experiment.
 *
 * @param context
 */
const buildLinkToSRP = (context: ExtendedContextStore) => {
  if (!isVariantEnabled(context, Experiment.Blossom_SRP_Lite_V2)) {
    return null;
  }

  const link = new HotelSearchLinkBuilder()
    .withDaysInFuture()
    .withStayLength()
    .withExpandForm()
    .withRegionId(context.searchContext?.location?.id);

  // Add filters to SRP link
  const filters = context.searchContext?.affinity?.hsrFilters;
  if (filters) {
    Object.keys(filters).forEach((key) => {
      link.withFilter(key, filters[key]);
    });
  }

  return link.build(context);
};
